import {ElementType, forwardRef, FunctionComponent, ReactNode} from 'react'
import type {IconType} from 'react-icons'
import clsx from 'clsx'
import {
    PolymorphicComponentProps,
    PolymorphicComponentPropsWithRef,
    PolymorphicRef,
} from '~/components/common/types/PolymorphicComponentTypes'

interface InternalButtonProps {
    classNames?: {
        root?: string
        startIcon?: string
        endIcon?: string
    }
    loader?: {
        loading: boolean
        label: string
    }
    startIcon?: IconType
    endIcon?: IconType
    variant?: 'primary' | 'secondary'
}

type InternalButtonPropsWithRef<C extends ElementType = 'button'> =
    PolymorphicComponentPropsWithRef<C, InternalButtonProps>

interface ButtonFC extends FunctionComponent {
    <C extends ElementType = 'button'>(props: InternalButtonPropsWithRef<C>): ReactNode
}

export const Button: ButtonFC = forwardRef(
    <T extends ElementType = 'button'>(
        {
            children,
            classNames,
            className,
            loader,
            as,
            startIcon: StartIcon,
            endIcon: EndIcon,
            variant = 'primary',
            ...rest
        }: PolymorphicComponentProps<T, InternalButtonProps>,
        ref: PolymorphicRef<'button'>
    ): ReactNode => {
        const {href, to} = rest
        const Tag = as ?? (href || to ? 'a' : 'button')

        return (
            <Tag
                {...rest}
                ref={ref}
                className={clsx(
                    'relative px-4 py-3  font-medium rounded-lg shadow-sm backdrop-blur-lg transform-gpu flex items-center justify-center overflow-hidden',
                    className,
                    classNames?.root,
                    {
                        'from-violet-700/75 to-violet-800/90 bg-gradient-to-b border text-white border-violet-800 shadow-violet-900':
                            variant === 'primary',
                        'bg-gray-500/10 backdrop-blur-sm text-green-600 border border-green-600 shadow-green-900':
                            variant === 'secondary',
                    }
                )}
            >
                <div
                    className={clsx(
                        'flex items-center justify-center gap-4',
                        loader?.loading && 'opacity-0'
                    )}
                    aria-hidden={!!loader?.loading}
                >
                    {!!StartIcon && (
                        <StartIcon
                            aria-hidden
                            className={clsx('size-6', classNames?.startIcon)}
                            fill="currentColor"
                            role="img"
                        />
                    )}
                    {children}
                    {!!EndIcon && (
                        <EndIcon
                            aria-hidden
                            className={clsx('size-6', classNames?.endIcon)}
                            fill="currentColor"
                            role="img"
                        />
                    )}
                </div>
                {!!loader && (
                    <>
                        <div
                            className={clsx('absolute', !loader.loading && 'opacity-0')}
                            aria-hidden={!loader.loading}
                        >
                            {loader.label}
                        </div>
                        <div
                            aria-hidden="true"
                            className={clsx(
                                'absolute left-0 top-0 h-full bg-black/25 motion-reduce:hidden w-0 opacity-100',
                                loader.loading &&
                                    '!w-full !opacity-0 transition-all duration-3000 ease-in delay-300'
                            )}
                        />
                    </>
                )}
            </Tag>
        )
    }
)

Button.displayName = 'Button'
Button.defaultProps = {
    as: 'button',
}
